import React, { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Upload, Camera, PenLine, Image, ChevronDown, Loader2, FileText,
} from 'lucide-react';
import { api } from '../../api/client';
import { formatCalendarDate, tenantTodayDate, todayCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency } from '../../lib/display-labels';
import { useLocale } from '../../contexts/LocaleContext';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface LineItem { description: string; amount: number }

interface ExtractedReceipt {
  vendor: string; date: string; invoiceRef: string;
  lineItems: LineItem[]; subtotal: number; gst: number; total: number;
  category: string;
}

interface Expense {
  id: string;
  date: string;
  description: string;
  vendor: string;
  amount: number;
  category: string;
  receiptUrl: string | null;
}

interface SummaryData {
  totalAmount: number;
  byCategory: Record<string, number>;
  costPerKm: number | null;
}

interface AssetExpensesProps {
  equipmentId: string;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const CATEGORIES = ['Maintenance', 'Fuel', 'Repair', 'Tires', 'Registration', 'Insurance', 'Other'];

const CAT_CLS: Record<string, string> = {
  maintenance: 'bg-amber-500/15 text-amber-400',
  fuel:        'bg-fieldiq-accent/15 text-fieldiq-accent',
  repair:      'bg-red-500/15 text-red-400',
  tires:       'bg-green-500/15 text-green-400',
  registration:'bg-blue-500/15 text-blue-400',
  insurance:   'bg-purple-500/15 text-purple-400',
  other:       'bg-fieldiq-dark text-gray-400',
};

const inputClass =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 transition-colors';

/** @see fmtCurrency — absence renders '—', not '$0.00'. */
const fmtMoney = (n: number | string | null | undefined) => fmtCurrency(n);

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function AssetExpenses({ equipmentId }: AssetExpensesProps) {
  const queryClient = useQueryClient();
  const { isMetric } = useLocale();
  const distanceUnit = isMetric ? 'km' : 'mi';
  const fileRef = useRef<HTMLInputElement>(null);

  /* ---- local form state ---- */
  const [mode, setMode] = useState<'idle' | 'ocr' | 'manual'>('idle');
  const [uploading, setUploading] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedReceipt | null>(null);
  const [category, setCategory] = useState('Maintenance');
  const [odometer, setOdometer] = useState('');
  const [notes, setNotes] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualVendor, setManualVendor] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const timeZone = useTenantTimeZone();
  // A date-input seed, so '' (an empty picker) is the absent case — never a guessed day.
  const [manualDate, setManualDate] = useState(todayCalendarDate(timeZone) ?? '');

  /* ---- queries ---- */
  const { data: raw, isLoading } = useQuery({
    queryKey: ['asset-expenses', equipmentId],
    queryFn: () =>
      api.assetExpenses.list({ equipmentId }).then(r => {
        const body = r.data?.data ?? r.data ?? {};
        const expenses: Expense[] = (body.expenses ?? []).map((e: any) => ({
          id: e.id,
          date: e.expense_date ?? e.expenseDate ?? '',
          description: e.description ?? '',
          vendor: e.vendor ?? '',
          amount: Number(e.amount ?? 0),
          category: e.category ?? 'other',
          receiptUrl: e.receipt_url ?? e.receiptUrl ?? null,
        }));
        const s = body.summary ?? {};
        const summary: SummaryData = {
          totalAmount: Number(s.total_amount ?? s.totalAmount ?? 0),
          byCategory: s.by_category ?? s.byCategory ?? {},
          costPerKm: s.cost_per_km ?? s.costPerKm ?? null,
        };
        return { expenses, summary };
      }),
    enabled: !!equipmentId,
  });

  const expenses = raw?.expenses ?? [];
  const summary = raw?.summary ?? { totalAmount: 0, byCategory: {}, costPerKm: null };

  /* ---- mutations ---- */
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['asset-expenses', equipmentId] });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, any>) => api.assetExpenses.create(data),
    onSuccess: () => {
      invalidate();
      resetForm();
      toast.success('Expense saved');
    },
    onError: () => toast.error('Failed to save expense'),
  });

  /* ---- OCR upload ---- */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMode('ocr');
    try {
      const fd = new FormData();
      // The route reads `body['receipt']` (asset-expenses.routes.ts) and 400s
      // with "receipt file is required" on any other key. This said 'file', so
      // every upload from this panel failed; the toast blamed the extraction.
      fd.append('receipt', file);
      fd.append('equipment_id', equipmentId);
      // Do NOT set Content-Type — the client.ts interceptor strips it so the
      // browser sets the multipart boundary automatically.
      const res = await api.assetExpenses.ocr(fd);
      const d = res.data?.data ?? res.data ?? {};
      setExtracted({
        vendor: d.vendor ?? '',
        date: d.date ?? d.expense_date ?? '',
        invoiceRef: d.invoice_ref ?? d.invoiceRef ?? '',
        lineItems: (d.line_items ?? d.lineItems ?? []).map((li: any) => ({
          description: li.description ?? '', amount: Number(li.amount ?? 0),
        })),
        subtotal: Number(d.subtotal ?? 0),
        gst: Number(d.gst ?? d.tax ?? 0),
        total: Number(d.total ?? 0),
        // `category_suggestion` is the key the route sends; `category` is not a
        // field it returns, so this always fell through to the default.
        category: d.category_suggestion ?? d.category ?? 'Maintenance',
      });
      const suggested = String(d.category_suggestion ?? d.category ?? '');
      setCategory(CATEGORIES.find(c => c.toLowerCase() === suggested.toLowerCase()) ?? 'Maintenance');
      toast.success('Receipt scanned');
    } catch {
      toast.error('OCR extraction failed');
      setMode('idle');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /* ---- save ---- */
  const handleSaveOcr = () => {
    if (!extracted) return;
    createMutation.mutate({
      equipment_id: equipmentId,
      amount: extracted.total,
      category: category.toLowerCase(),
      vendor: extracted.vendor,
      description: extracted.lineItems.map(li => li.description).join('; '),
      expense_date: extracted.date,
      invoice_ref: extracted.invoiceRef,
      odometer_reading: odometer || undefined,
      notes: notes || undefined,
    });
  };

  const handleSaveManual = () => {
    const amt = parseFloat(manualAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    createMutation.mutate({
      equipment_id: equipmentId,
      amount: amt,
      category: category.toLowerCase(),
      vendor: manualVendor || undefined,
      description: manualDescription || undefined,
      expense_date: manualDate,
      odometer_reading: odometer || undefined,
      notes: notes || undefined,
    });
  };

  const resetForm = () => {
    setMode('idle');
    setExtracted(null);
    setCategory('Maintenance');
    setOdometer('');
    setNotes('');
    setManualAmount('');
    setManualVendor('');
    setManualDescription('');
    setManualDate(todayCalendarDate(timeZone) ?? '');
  };

  /* ---- loading state ---- */
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-fieldiq-accent" />
      </div>
    );
  }

  /* ---- category helpers ---- */
  const catEntries = Object.entries(summary.byCategory);
  const topCategories = catEntries.length > 0
    ? catEntries
        .sort(([, a], [, b]) => (b as number) - (a as number))
        .slice(0, 3)
    : [];

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <div className="text-white">
      <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileChange} />

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Left — Capture & Entry */}
        <div className="flex-1 space-y-4 min-w-0">
          {/* Capture zone */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Capture Receipt or Invoice</p>
            <div className="bg-fieldiq-card border-2 border-dashed border-fieldiq-border rounded-xl p-6 text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-lg bg-fieldiq-dark flex items-center justify-center">
                {uploading
                  ? <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
                  : <Image className="w-6 h-6 text-gray-400" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-white">
                  {uploading ? 'Scanning receipt…' : 'Scan or upload receipt / invoice'}
                </p>
                <p className="text-[11px] text-gray-400">AI extracts vendor, amount, date and line items automatically</p>
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Camera className="w-3.5 h-3.5" /> Take photo
                </button>
                <button
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="px-3 py-1.5 text-xs font-semibold bg-fieldiq-accent text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Upload className="w-3.5 h-3.5" /> Upload file
                </button>
                <button
                  disabled={uploading}
                  onClick={() => { resetForm(); setMode('manual'); }}
                  className="px-3 py-1.5 text-xs font-semibold border border-fieldiq-border rounded-lg text-white flex items-center gap-1.5 hover:bg-fieldiq-card disabled:opacity-50"
                >
                  <PenLine className="w-3.5 h-3.5" /> Enter manually
                </button>
              </div>
            </div>
          </div>

          {/* OCR extracted receipt */}
          {mode === 'ocr' && extracted && (
            <div className="space-y-4">
              <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-green-500/15 text-green-400">AI extracted</span>
                  <span className="text-[11px] text-gray-400">Review and confirm — tap any field to edit</span>
                </div>
                <div className="bg-fieldiq-dark border border-fieldiq-border rounded-xl p-4 space-y-1.5">
                  <div className="flex justify-between text-xs text-gray-400 mb-2">
                    <span>Vendor</span>
                    <span className="font-semibold text-white">{extracted.vendor}</span>
                  </div>
                  {extracted.lineItems.map((li, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-gray-400">{li.description}</span>
                      <span className="font-medium text-white">{fmtMoney(li.amount)}</span>
                    </div>
                  ))}
                  <div className="border-t border-fieldiq-border pt-2 mt-2 flex justify-between text-sm font-bold">
                    <span className="text-white">Total</span>
                    <span className="text-green-400">{fmtMoney(extracted.total)}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {[
                    { l: 'VENDOR', v: extracted.vendor },
                    { l: 'DATE', v: extracted.date },
                    { l: 'INVOICE REF', v: extracted.invoiceRef },
                  ].map(s => (
                    <div key={s.l} className="bg-fieldiq-dark border border-fieldiq-border rounded-xl p-2.5">
                      <p className="text-[10px] text-gray-400 font-semibold">{s.l}</p>
                      <p className="text-xs font-bold text-white">{s.v || '—'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category & odometer */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Category</p>
                  <div className="relative">
                    <select value={category} onChange={e => setCategory(e.target.value)} className={inputClass + ' appearance-none pr-8'}>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Odometer at Service ({distanceUnit})</p>
                  <input value={odometer} onChange={e => setOdometer(e.target.value)} placeholder="Optional" className={inputClass} />
                </div>
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Notes</p>
                <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" className={inputClass} />
              </div>

              <div className="space-y-2 pt-1">
                <button
                  disabled={createMutation.isPending}
                  onClick={handleSaveOcr}
                  className="w-full py-3 text-sm font-bold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Saving…' : `Save expense — ${fmtMoney(extracted.total)}`}
                </button>
                <button onClick={resetForm} className="w-full py-2.5 text-sm font-semibold border border-fieldiq-border rounded-lg hover:bg-fieldiq-card text-white">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Manual entry form */}
          {mode === 'manual' && (
            <div className="space-y-4">
              <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-white">Manual Expense Entry</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Amount ($)</p>
                    <input type="number" step="0.01" min="0" value={manualAmount} onChange={e => setManualAmount(e.target.value)} placeholder="0.00" className={inputClass} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Date</p>
                    <input type="date" value={manualDate} onChange={e => setManualDate(e.target.value)} className={inputClass} />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Vendor</p>
                  <input value={manualVendor} onChange={e => setManualVendor(e.target.value)} placeholder="e.g. Nisku Truck Centre" className={inputClass} />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Description</p>
                  <input value={manualDescription} onChange={e => setManualDescription(e.target.value)} placeholder="e.g. Oil & filter change" className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Category</p>
                    <div className="relative">
                      <select value={category} onChange={e => setCategory(e.target.value)} className={inputClass + ' appearance-none pr-8'}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Odometer ({distanceUnit})</p>
                    <input value={odometer} onChange={e => setOdometer(e.target.value)} placeholder="Optional" className={inputClass} />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Notes</p>
                  <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" className={inputClass} />
                </div>
              </div>

              <div className="space-y-2 pt-1">
                <button
                  disabled={createMutation.isPending}
                  onClick={handleSaveManual}
                  className="w-full py-3 text-sm font-bold bg-fieldiq-accent text-white rounded-lg disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Saving…' : 'Save expense'}
                </button>
                <button onClick={resetForm} className="w-full py-2.5 text-sm font-semibold border border-fieldiq-border rounded-lg hover:bg-fieldiq-card text-white">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right — Cost Summary */}
        <div className="lg:w-[380px] shrink-0 space-y-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Cost Summary</p>

          {/* YTD + Cost/km */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-3">
              <p className="text-[10px] text-gray-400 font-semibold">YTD COSTS</p>
              <p className="text-xl font-bold text-red-400">
                {summary.totalAmount > 0 ? fmtMoney(summary.totalAmount) : '$0.00'}
              </p>
              <p className="text-[10px] text-gray-400">
                {tenantTodayDate(timeZone)?.getFullYear() ?? '—'} year to date
              </p>
            </div>
            <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-3">
              <p className="text-[10px] text-gray-400 font-semibold">COST / KM</p>
              <p className="text-xl font-bold text-white">
                {summary.costPerKm != null && isFinite(summary.costPerKm)
                  ? `$${summary.costPerKm.toFixed(3)}`
                  : '—'}
              </p>
              <p className="text-[10px] text-gray-400">Rolling 12 mo.</p>
            </div>
          </div>

          {/* Category breakdown */}
          {topCategories.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {topCategories.map(([cat, val]) => (
                <div key={cat} className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-3 text-center">
                  <p className="text-[10px] text-gray-400 font-semibold capitalize">{cat}</p>
                  <p className="text-lg font-bold text-white">{fmtMoney(val as number)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Expenses list */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">
              {expenses.length > 0 ? 'Recent Expenses' : 'Expenses'}
            </p>

            {expenses.length === 0 ? (
              <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-8 text-center space-y-2">
                <FileText className="w-8 h-8 text-gray-500 mx-auto" />
                <p className="text-sm font-semibold text-white">No expenses yet</p>
                <p className="text-xs text-gray-400">
                  Add one manually or upload a receipt to get started.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {expenses.map(exp => (
                  <div key={exp.id} className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-3 flex items-center gap-3">
                    <span className="text-[11px] text-gray-400 w-16 shrink-0 font-medium">
                      {formatCalendarDate(exp.date, { month: 'short', day: 'numeric' }) ?? '—'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-white truncate">{exp.description || '—'}</p>
                      <p className="text-[10px] text-gray-400 truncate">{exp.vendor || '—'}</p>
                    </div>
                    <span className="text-sm font-bold text-white shrink-0">{fmtMoney(exp.amount)}</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${CAT_CLS[exp.category] ?? CAT_CLS.other}`}>
                      {exp.category}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
